import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import TopNav from "../components/TopNav";
import { COURSE } from "../data/course";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import {
  BookOpen,
  Network,
  Compass,
  LayoutGrid,
  Workflow,
  BarChart3,
  ShieldCheck,
  Rocket,
  GraduationCap,
  Award,
  Clock,
  ArrowRight,
  CheckCircle2,
  Bookmark,
} from "lucide-react";

const ICONS = {
  BookOpen,
  Network,
  Compass,
  LayoutGrid,
  Workflow,
  BarChart3,
  ShieldCheck,
  Rocket,
  GraduationCap,
  Award,
};

export default function Home() {
  const { user, progress, percent, allModulesPassed, completedMods, totalModules } = useApp();
  const nav = useNavigate();

  useEffect(() => {
    if (!user) nav("/");
  }, [user, nav]);

  useEffect(() => {
    if (allModulesPassed) {
      window.parent.postMessage({ type: "COURSE_COMPLETED" }, "*");
    }
  }, [allModulesPassed]);

  if (!user) return null;

  return (
    <div className="min-h-screen bg-background paper-texture" data-testid="home-page">
      <TopNav />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        {/* Hero */}
        <section className="mb-12">
          <div className="mono text-[11px] uppercase tracking-[0.3em] text-accent">
            Welcome back
          </div>
          <h1 className="serif text-5xl sm:text-6xl leading-tight mt-2">
            Hello, {user.name.split(" ")[0]}.
          </h1>
          <p className="mt-3 text-lg text-muted-foreground max-w-2xl">
            {COURSE.title} — {COURSE.subtitle}. Explore each module at your own pace.
            Your progress is saved automatically.
          </p>

          <div className="mt-6 rounded-md border border-border bg-card p-5 max-w-xl">
            <div className="flex items-center justify-between mb-2">
              <span className="mono text-xs uppercase tracking-widest text-muted-foreground">
                Course Progress
              </span>
              <span className="mono text-sm font-bold">{percent}%</span>
            </div>
            <Progress value={percent} className="h-2" data-testid="home-progress-bar" />
            <div className="mt-3 text-sm text-muted-foreground flex flex-wrap items-center gap-4">
              <span>
                <span className="font-semibold text-foreground">{completedMods}</span> / {totalModules} modules passed
              </span>
              <span>•</span>
              <span>{progress?.bookmarks?.length || 0} bookmarks</span>
              {allModulesPassed && (
                <>
                  <span>•</span>
                  <span className="text-emerald-600 font-bold">100% Complete</span>
                </>
              )}
            </div>
          </div>
        </section>

        {/* Objectives */}
        <section className="mb-12">
          <h2 className="serif text-3xl mb-4">What you will gain</h2>
          <ul className="grid sm:grid-cols-2 gap-3">
            {COURSE.objectives.map((o) => (
              <li
                key={o}
                className="flex items-start gap-2 rounded-md border border-border bg-card/60 p-4"
              >
                <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                <span className="text-sm">{o}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Modules bento */}
        <section className="mb-12">
          <div className="flex items-end justify-between mb-5">
            <div>
              <div className="mono text-[11px] uppercase tracking-[0.3em] text-accent mb-1">
                Curriculum
              </div>
              <h2 className="serif text-4xl">9 Modules + Capstone</h2>
            </div>
            <div className="hidden sm:flex items-center gap-1 text-sm text-muted-foreground">
              <Bookmark className="w-4 h-4" />
              <Link to="/bookmarks" className="underline">
                Saved subtopics
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {COURSE.modules.map((m) => {
              const Icon = ICONS[m.icon] || BookOpen;
              const done = progress?.completed_modules?.includes(m.id);
              const score = progress?.quiz_scores?.[m.id];
              return (
                <Card
                  key={m.id}
                  className="card-hover p-6 flex flex-col"
                  data-testid={`module-card-${m.id}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="mono text-xs text-muted-foreground">
                      Module {m.number}
                    </span>
                    <span className="mono text-xs text-muted-foreground inline-flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {m.duration}
                    </span>
                  </div>
                  <div className="mt-3 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-primary" />
                    </div>
                    <h3 className="serif text-xl leading-snug">{m.title}</h3>
                  </div>
                  <p className="text-sm text-muted-foreground mt-3 flex-1">
                    {m.tagline}
                  </p>
                  <div className="mt-4 rounded-md bg-secondary border border-border p-3">
                    <span className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
                      Key Takeaway
                    </span>
                    <p className="text-sm mt-1 leading-snug">{m.takeaway}</p>
                  </div>
                  <div className="mt-4 flex items-center justify-between">
                    {done ? (
                      <span className="text-xs inline-flex items-center gap-1 text-emerald-600">
                        <CheckCircle2 className="w-4 h-4" />
                        Passed{score ? ` • ${score.percent}%` : ""}
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">
                        {m.subTopics.length} subtopics • {m.quiz.length} quiz Qs
                      </span>
                    )}
                    <Button
                      size="sm"
                      variant={done ? "outline" : "default"}
                      onClick={() => nav(`/module/${m.id}`)}
                      data-testid={`module-open-btn-${m.id}`}
                    >
                      {done ? "Review" : "Start"} <ArrowRight className="w-3.5 h-3.5 ml-1" />
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        </section>

        {/* Certificate callout */}
        {allModulesPassed && (
          <section className="mb-8">
            <div className="rounded-md border border-emerald-500/40 bg-emerald-500/5 p-6 flex flex-col sm:flex-row sm:items-center gap-4">
              <CheckCircle2 className="w-12 h-12 text-emerald-500" />
              <div className="flex-1">
                <h3 className="serif text-2xl text-emerald-600">Module Complete!</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  You may now return to the main LMS dashboard to view your official certificate.
                </p>
              </div>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
